import { forwardRef, useRef, useEffect, useMemo, useState } from 'react'
import { useGLTF, useFBX, useAnimations } from '@react-three/drei'
import * as THREE from 'three'
import { VRMLoaderPlugin } from '@pixiv/three-vrm';
import { mixamoVRMRigMap, normalizedVRMRigMap } from './mixamoVRMRigMap';
import { EffectComposer, Outline } from '@react-three/postprocessing'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Lipsync } from './lipsync';
import { suspend } from 'suspend-react';
import { DRACOLoader } from 'three-stdlib';
import { BlendFunction } from 'postprocessing';
const loaderFunction = (loader) => {
  loader.register((parser) => new VRMLoaderPlugin(parser));
};

const COMPANION_BASE_X = 0.8;

const DRONE_BASE_X = -1;
const DRONE_BASE_Y = 1.45;

const SEC_WEAPON_BASE_X = -0.20;//-1;
const SEC_WEAPON_BASE_Y = 0;//0.45;
const SEC_WEAPON_BASE_Z = 0.125;//0.45;
const SEC_WEAPON_ROTATION_X = Math.PI / 2;
const SEC_WEAPON_ROTATION_Y = -Math.PI / 3;
const SEC_WEAPON_ROTATION_Z = 0;

const AUX_WEAPON_BASE_X = 0.20;//1;
const AUX_WEAPON_BASE_Y = 0;//0.45;
const AUX_WEAPON_BASE_Z = 0.125;//0.45;
const AUX_WEAPON_ROTATION_X = Math.PI / 2;
const AUX_WEAPON_ROTATION_Y = Math.PI / 3;
const AUX_WEAPON_ROTATION_Z = Math.PI;

const SEC_L_WEAPON_BASE_X = -0.20;//-1;
const SEC_L_WEAPON_BASE_Y = -0.15;//0.45;
const SEC_L_WEAPON_BASE_Z = 0;//0.45;
const SEC_L_WEAPON_ROTATION_X = 0;
const SEC_L_WEAPON_MELEE_ROTATION_X = -Math.PI / 2;
const SEC_L_WEAPON_ROTATION_Y = Math.PI / 30;
const SEC_L_WEAPON_ROTATION_Z = -Math.PI / 1.75;

const AUX_L_WEAPON_BASE_X = 0.20;//1;
const AUX_L_WEAPON_BASE_Y = -0.15;//0.45;
const AUX_L_WEAPON_BASE_Z = 0;//0.45;
const AUX_L_WEAPON_ROTATION_X = 0;
const AUX_L_WEAPON_MELEE_ROTATION_X = -Math.PI / 2;
const AUX_L_WEAPON_ROTATION_Y = -Math.PI / 30;
const AUX_L_WEAPON_ROTATION_Z = -Math.PI / 2.25;

const retargetMixamoAnimForVRM = (gltf, vrm, name, options = {}) => {
    const { clipName = null, clipIndex = 0} = options;

    const asset = gltf.scene || gltf;
    asset.animations = gltf.animations;

    const clip = clipName != null
        ? THREE.AnimationClip.findByName(asset.animations, clipName).clone()
        : asset.animations[clipIndex || 0].clone();

    const tracks = []; // KeyframeTracks compatible with VRM will be added here
    const restRotationInverse = new THREE.Quaternion();
    const parentRestWorldRotation = new THREE.Quaternion();
    const _quatA = new THREE.Quaternion();
    const _vec3 = new THREE.Vector3();

    if (!vrm) {
      return { name: 'none', tracks: [] };
    }

    let motionHipsHeight = asset.getObjectByName("mixamorigHips")?.position.y;
    if (motionHipsHeight == null) {
        motionHipsHeight = asset.getObjectByName("Hips")?.position.y;
    }
    const vrmHipsY = vrm.humanoid?.getNormalizedBoneNode("hips").getWorldPosition(_vec3).y;
    const vrmRootY = vrm.scene.getWorldPosition(_vec3).y;
    const vrmHipsHeight = Math.abs(vrmHipsY - vrmRootY);
    const hipsPositionScale = vrmHipsHeight / motionHipsHeight;

    clip.tracks = clip.tracks.filter((track) =>
        track.name.toLowerCase().includes(".quaternion")//);
        || (track.name.toLowerCase().includes(".position") && track.name.toLowerCase().includes("hips")));

    clip.tracks.forEach((track) => {

        const trackSplitted = track.name.split(".");
        const mixamoRigName = trackSplitted[0];
        let vrmBoneName = mixamoVRMRigMap[mixamoRigName];
        if (!vrmBoneName) {
            vrmBoneName = normalizedVRMRigMap[mixamoRigName];
        }

        let vrmNodeName = vrm.humanoid?.getNormalizedBoneNode(vrmBoneName)?.name;

        vrmNodeName = vrmNodeName?.replace("Normalized_", "")
        if (vrmNodeName) {
            const mixamoRigNode = asset.getObjectByName(mixamoRigName);
            const propertyName = trackSplitted[1];

            // Store rotations of rest-pose.
            mixamoRigNode.getWorldQuaternion(restRotationInverse).invert();
            mixamoRigNode.parent.getWorldQuaternion(parentRestWorldRotation);

            if (track instanceof THREE.QuaternionKeyframeTrack) {
                // Retarget rotation of mixamoRig to NormalizedBone.
                for (let i = 0; i < track.values.length; i += 4) {
                    const flatQuaternion = track.values.slice(i, i + 4);

                    _quatA.fromArray(flatQuaternion);
                    _quatA
                        .premultiply(parentRestWorldRotation)
                        .multiply(restRotationInverse);

                    _quatA.toArray(flatQuaternion);

                    flatQuaternion.forEach((v, index) => {
                        track.values[index + i] = v;
                    });
                }

                tracks.push(
                    new THREE.QuaternionKeyframeTrack(
                        `${vrmNodeName}.${propertyName}`,
                        track.times,
                        track.values.map((v, i) => (vrm.meta?.metaVersion === "0" && i % 2 === 0 ? - v : v)),
                    ),
                );

            } else if (track instanceof THREE.VectorKeyframeTrack) {
                const value = track.values.map((v, i) => (vrm.meta?.metaVersion === "0" && i % 3 !== 1 ? - v : v) * hipsPositionScale);
                tracks.push(new THREE.VectorKeyframeTrack(`${vrmNodeName}.${propertyName}`, track.times, value));
            }
        }

    });

    return new THREE.AnimationClip(`vrmAnimation_${name}`, clip.duration, tracks);
}

const timeBeweetBlinks = 3000;

let dracoLoader = null;

const loadItem = async (itemPath, zoomFactor = null) => {
  if (!dracoLoader) {
    dracoLoader = new DRACOLoader();
  }
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.5/');

  if (!itemPath) {
    return null
  }
  const isGltf = !itemPath.endsWith('fbx');
  const loader = isGltf ? new GLTFLoader() : new FBXLoader();
  loader.setDRACOLoader(dracoLoader);
  const gltf = await loader.loadAsync(itemPath);
  if (!isGltf) {
    gltf.scale.set(0.01, 0.01, 0.01);
  } else if (zoomFactor) {
    gltf.scene.scale.set(zoomFactor, zoomFactor, zoomFactor);
  }
  const sc = gltf.scene || gltf;
  const clearPath = itemPath.split('?')[0];
  if (!clearPath.endsWith('.vrm')) {
    sc.rotation.y = Math.PI;
  }
  return sc;
};

const BaseFigure = forwardRef((props, ref) => {
  const { glbPath, weapons = [], companionGlbPath = null, droneGlbPath = null, armorGlbPath = null, objectGlbPath = null, droneType = null, objectType = null, controls = null, consumeObjectPath = null, onFinished = () => {}, onPlaybackStart = () => {}, onPlaybackStop = () => {}, onLipsyncCreated = () => {}, idleAnimationClipName = 'none', isDrunk = false } = props;
  const selectedWeapon = weapons && weapons.length > 0 ? weapons[0] : null;
  const { weaponType = 'heavy', weaponGlbPath = null } = selectedWeapon || {};
  const secondWeapon = (weapons && weapons.length > 1 && weapons[1].effects.includes('TWO_HANDED')) ? weapons[1] : null;
  const thirdWeapon = (weapons && weapons.length > 2 && weapons[2].effects.includes('TWO_HANDED')) ? weapons[2] : null;
  const secondLightWeapon = (weapons && weapons.length > 1 && !weapons[1].effects.includes('TWO_HANDED')) ? weapons[1] : null;
  const thirdLightWeapon = (weapons && weapons.length > 2 && !weapons[2].effects.includes('TWO_HANDED')) ? weapons[2] : null;
  const { scene, nodes, materials, userData } = useGLTF(glbPath, true, true, loaderFunction);
  const [firstLoad, setFirstLoad] = useState(true);
  const lipsync = useRef();
  const managerVrm = useRef();
  const lastBlink = useRef(0);
  const clockRef = useRef(new THREE.Clock());

  const asset = useGLTF('/assets/animations/Idle_Holding_Art.glb');
  const heavyWeaponHoldAsset = useGLTF('/assets/animations/Idle_HeavyWeapon_Hold.glb');
  const heavyWeaponFreeAsset = useGLTF('/assets/animations/Idle_HeavyWeapon_FreeHand.glb');
  const lightWeaponHoldAsset = useGLTF('/assets/animations/Idle_LightWeapon_Hold.glb');
  const lightWeaponFreeAsset = useGLTF('/assets/animations/Idle_LightWeapon_FreeHand.glb');
  const meleeAsset = useGLTF('/assets/animations/Idle_SwordShield_Art.glb');
  const danceAsset = useGLTF('/assets/animations/Idle_Dance.glb');
  const drunkAsset = useFBX('/assets/animations/drunk.fbx');
  const consumeAsset = useFBX('/assets/animations/Drinking.fbx');

  const idleClip = useMemo(() => retargetMixamoAnimForVRM(asset, userData.vrm, 'none'), []);
  const hwhClip = useMemo(() => retargetMixamoAnimForVRM(heavyWeaponHoldAsset, userData.vrm, 'hw_h'), []);
  const hwfClip = useMemo(() => retargetMixamoAnimForVRM(heavyWeaponFreeAsset, userData.vrm, 'hw_f'), []);
  const lwhClip = useMemo(() => retargetMixamoAnimForVRM(lightWeaponHoldAsset, userData.vrm, 'lw_h'), []);
  const lwfClip = useMemo(() => retargetMixamoAnimForVRM(lightWeaponFreeAsset, userData.vrm, 'lw_f'), []);
  const meleeClip = useMemo(() => retargetMixamoAnimForVRM(meleeAsset, userData.vrm, 'melee'), []);
  const danceClip = useMemo(() => retargetMixamoAnimForVRM(danceAsset, userData.vrm, 'dance'), []);
  const drunkClip = useMemo(() => retargetMixamoAnimForVRM(drunkAsset, userData.vrm, 'drunk'), []);
  const consumeClip = useMemo(() => retargetMixamoAnimForVRM(consumeAsset, userData.vrm, 'consume'), []);

  let animationType = idleAnimationClipName;

  if (objectGlbPath) {
    animationType = weaponType === 'heavy' ? 'hw_h' : 'lw_h';
  } else if (weaponGlbPath) {
    animationType = weaponType === 'heavy' ? 'hw_f' : 'lw_f';
  }

  if (objectGlbPath && weaponGlbPath && weaponType === 'melee' && objectType === 'shield') {
    animationType = 'melee';
  }

  if (!objectGlbPath && !weaponGlbPath && droneGlbPath && droneType === 'dance') {
    animationType = 'dance';
  }
  if (isDrunk) {
    animationType = 'drunk';
  }

  // Override current animation by temporary displaying consume animation of selected object
  if (consumeObjectPath) {
    animationType = 'consume'
  }
  
  const modelAnimations = useAnimations([idleClip, hwhClip, hwfClip, lwfClip, lwhClip, danceClip, meleeClip, consumeClip, drunkClip], scene);
  const action = modelAnimations.actions[`vrmAnimation_${animationType}`];
  const leftHandObjectBone = useRef(scene.getObjectByName('LeftHand_Gun'));
  const rightHandWeaponBone = useRef(scene.getObjectByName('RightHand_Gun'));
  const leftShoulderBone = useRef(scene.getObjectByName('LeftShoulder'));
  const rightShoulderBone = useRef(scene.getObjectByName('RightShoulder'));
  const hipsBone = useRef(scene.getObjectByName('Hips'));
  const armorRef = useRef(null);
  const droneRef = useRef(null);
  const secondaryWeaponRef = useRef(null);
  const auxWeaponRef = useRef(null);
  const secondaryLightWeaponRef = useRef(null);
  const auxLightWeaponRef = useRef(null);
  const companionRef = useRef(null);
  const sceneRef = useRef(scene);
  const weapon = suspend(async () => loadItem(weaponGlbPath), [weaponGlbPath]);

  const secondaryWeapon = suspend(async () => loadItem(secondWeapon?.weaponGlbPath), [secondWeapon?.weaponGlbPath]);
  const auxWeapon = suspend(async () => loadItem(thirdWeapon?.weaponGlbPath), [thirdWeapon?.weaponGlbPath]);

  const secondaryLightWeapon = suspend(async () => loadItem(secondLightWeapon?.weaponGlbPath), [secondLightWeapon?.weaponGlbPath]);
  const auxLightWeapon = suspend(async () => loadItem(thirdLightWeapon?.weaponGlbPath), [thirdLightWeapon?.weaponGlbPath]);

  const activeObjectPath = consumeObjectPath || objectGlbPath;
  const object = suspend(async () => loadItem(activeObjectPath), [activeObjectPath]);
  const armor = suspend(async () => loadItem(armorGlbPath), [armorGlbPath]);
  const drone = suspend(async () => loadItem(droneGlbPath, droneType === 'mutatio' ? 10 : null), [droneGlbPath]);
  const companion = suspend(async () => loadItem(companionGlbPath), [companionGlbPath]);

  const syncArmor = () => {
    if (armorRef.current) {
      const armorHips = armorRef.current.getObjectByName('Hips');
      if (armorHips) {
        const applyBone = (bone) => {
          const refBone = sceneRef.current.getObjectByName(bone.name);
          if (refBone && bone.isBone) {
            bone.position.copy(refBone.position);
            bone.rotation.copy(refBone.rotation);
          }
          bone.children.forEach(b => applyBone(b));
        };
        applyBone(armorHips);
      }
    }
  };

  useEffect(() => {
    if (!sceneRef.current) {
      return;
    }

    let armorBone = sceneRef.current.getObjectByName('ArmorBone');
    if (!armorBone) {
      armorBone = new THREE.Bone();
      armorBone.name = 'ArmorBone';
      sceneRef.current.add(armorBone);
    }

    const findMeshes = (obj) => {
      const meshes = [];
      if (obj.isMesh) {
        meshes.push(obj);
      }
      obj.children.forEach(c => {
        meshes.push(...findMeshes(c));
      });
      return meshes;
    };

    if (armorBone.children.length > 0) {
      armorBone.children.forEach(c => armorBone.remove(c));
      armorRef.current = null;
    }

    if (armor) {
        const meshes = findMeshes(armor);
        meshes.forEach(m => m.onBeforeRender = () => {
          syncArmor();
	});
        armorBone.add(armor);
        armorRef.current = armor;
    }
  }, [armor]);

  useEffect(() => {
    if (!sceneRef.current) {
      return;
    }

    let droneBone = sceneRef.current.getObjectByName('DroneBone');
    if (!droneBone) {
      droneBone = new THREE.Bone();
      droneBone.name = 'DroneBone';
      droneBone.position.y = DRONE_BASE_Y;
      droneBone.position.x = DRONE_BASE_X;
      sceneRef.current.add(droneBone);
    }

    if (droneBone.children.length > 0) {
      droneBone.children.forEach(c => droneBone.remove(c));
      droneRef.current = null;
    }

    if (drone) {
      droneBone.add(drone);
      droneRef.current = drone;
    }
  }, [drone]);

  useEffect(() => {
    if (!sceneRef.current) {
      return;
    }

    let companionBone = sceneRef.current.getObjectByName('CompanionBone');
    if (!companionBone) {
      companionBone = new THREE.Bone();
      companionBone.name = 'CompanionBone';
      companionBone.position.y = 0;
      companionBone.position.x = COMPANION_BASE_X;
      sceneRef.current.add(companionBone);
    }

    if (companionBone.children.length > 0) {
      companionBone.children.forEach(c => companionBone.remove(c));
      companionRef.current = null;
    }

    if (companion) {
      companionBone.add(companion);
      companionRef.current = companion;
    }
  }, [companion]);

  useEffect(() => {
    if (!rightHandWeaponBone.current) {
      return;
    }

    if (rightHandWeaponBone.current.children.length > 0) {
      rightHandWeaponBone.current.children.forEach(c => rightHandWeaponBone.current.remove(c));
    }

    if (weapon && weaponGlbPath) {
      rightHandWeaponBone.current.add(weapon);
    }
  }, [weapon]);

  useEffect(() => {
    if (!leftShoulderBone.current || !sceneRef.current) {
      return;
    }

    let secondaryWeaponBone = sceneRef.current.getObjectByName('SecondaryWeaponBone');
    if (!secondaryWeaponBone) {
      secondaryWeaponBone = new THREE.Bone();
      secondaryWeaponBone.name = 'SecondaryWeaponBone';
      secondaryWeaponBone.position.y = SEC_WEAPON_BASE_Y;
      secondaryWeaponBone.position.x = SEC_WEAPON_BASE_X;
      secondaryWeaponBone.position.z = SEC_WEAPON_BASE_Z;
      secondaryWeaponBone.rotation.x = SEC_WEAPON_ROTATION_X;
      secondaryWeaponBone.rotation.y = SEC_WEAPON_ROTATION_Y;
      secondaryWeaponBone.rotation.z = SEC_WEAPON_ROTATION_Z;
      leftShoulderBone.current.add(secondaryWeaponBone);
    }

    if (secondaryWeaponBone.children.length > 0) {
      secondaryWeaponBone.children.forEach(c => secondaryWeaponBone.remove(c));
      secondaryWeaponRef.current = null;
    }

    if (secondaryWeapon) {
      secondaryWeaponBone.add(secondaryWeapon);
      secondaryWeaponRef.current = secondaryWeapon;
    }
  }, [secondaryWeapon]);
  
  useEffect(() => {
    if (!rightShoulderBone.current || !sceneRef.current) {
      return;
    }

    let auxWeaponBone = sceneRef.current.getObjectByName('AuxWeaponBone');
    if (!auxWeaponBone) {
      auxWeaponBone = new THREE.Bone();
      auxWeaponBone.name = 'AuxWeaponBone';
      auxWeaponBone.position.y = AUX_WEAPON_BASE_Y;
      auxWeaponBone.position.x = AUX_WEAPON_BASE_X;
      auxWeaponBone.position.z = AUX_WEAPON_BASE_Z;
      auxWeaponBone.rotation.x = AUX_WEAPON_ROTATION_X;
      auxWeaponBone.rotation.y = AUX_WEAPON_ROTATION_Y;
      auxWeaponBone.rotation.z = AUX_WEAPON_ROTATION_Z;
      rightShoulderBone.current.add(auxWeaponBone);
    }

    if (auxWeaponBone.children.length > 0) {
      auxWeaponBone.children.forEach(c => auxWeaponBone.remove(c));
      auxWeaponRef.current = null;
    }

    if (auxWeapon) {
      auxWeaponBone.add(auxWeapon);
      auxWeaponRef.current = auxWeapon;
    }
  }, [auxWeapon]);

  useEffect(() => {
    if (!hipsBone.current || !sceneRef.current) {
      return;
    }

    let secondaryWeaponBone = sceneRef.current.getObjectByName('SecondaryLightWeaponBone');
    if (!secondaryWeaponBone) {
      secondaryWeaponBone = new THREE.Bone();
      secondaryWeaponBone.name = 'SecondaryLightWeaponBone';
      secondaryWeaponBone.position.y = SEC_L_WEAPON_BASE_Y;
      secondaryWeaponBone.position.x = SEC_L_WEAPON_BASE_X;
      secondaryWeaponBone.position.z = SEC_L_WEAPON_BASE_Z;
      secondaryWeaponBone.rotation.x = SEC_L_WEAPON_ROTATION_X;
      secondaryWeaponBone.rotation.y = SEC_L_WEAPON_ROTATION_Y;
      secondaryWeaponBone.rotation.z = SEC_L_WEAPON_ROTATION_Z;
      hipsBone.current.add(secondaryWeaponBone);
    }

    if (secondaryWeaponBone.children.length > 0) {
      secondaryWeaponBone.children.forEach(c => secondaryWeaponBone.remove(c));
      secondaryLightWeaponRef.current = null;
    }

    if (secondaryLightWeapon) {
      secondaryWeaponBone.rotation.x = secondLightWeapon.weaponType === 'melee' ? SEC_L_WEAPON_MELEE_ROTATION_X : SEC_L_WEAPON_ROTATION_X;
      secondaryWeaponBone.add(secondaryLightWeapon);
      secondaryLightWeaponRef.current = secondaryWeapon;
    }
  }, [secondaryLightWeapon]);
  
  useEffect(() => {
    if (!hipsBone.current || !sceneRef.current) {
      return;
    }

    let auxWeaponBone = sceneRef.current.getObjectByName('AuxLightWeaponBone');
    if (!auxWeaponBone) {
      auxWeaponBone = new THREE.Bone();
      auxWeaponBone.name = 'AuxLightWeaponBone';
      auxWeaponBone.position.y = AUX_L_WEAPON_BASE_Y;
      auxWeaponBone.position.x = AUX_L_WEAPON_BASE_X;
      auxWeaponBone.position.z = AUX_L_WEAPON_BASE_Z;
      auxWeaponBone.rotation.x = AUX_L_WEAPON_ROTATION_X;
      auxWeaponBone.rotation.y = AUX_L_WEAPON_ROTATION_Y;
      auxWeaponBone.rotation.z = AUX_L_WEAPON_ROTATION_Z;
      hipsBone.current.add(auxWeaponBone);
    }

    if (auxWeaponBone.children.length > 0) {
      auxWeaponBone.children.forEach(c => auxWeaponBone.remove(c));
      auxLightWeaponRef.current = null;
    }

    if (auxLightWeapon) {
      auxWeaponBone.rotation.x = thirdLightWeapon.weaponType === 'melee' ? AUX_L_WEAPON_MELEE_ROTATION_X : AUX_L_WEAPON_ROTATION_X;
      auxWeaponBone.add(auxLightWeapon);
      auxLightWeaponRef.current = auxWeapon;
    }
  }, [auxLightWeapon]);

  useEffect(() => {
    if (!leftHandObjectBone.current) {
      return;
    }

    if (leftHandObjectBone.current.children.length > 0) {
      leftHandObjectBone.current.children.forEach(c => leftHandObjectBone.current.remove(c));
    }

    if (object) {
      leftHandObjectBone.current.add(object);
    }
  }, [object]);


  const mixer = modelAnimations.mixer;
  useEffect(() => {
    let onLoopCallback;
    if (animationType === 'consume') {
      onLoopCallback = () => onFinished(animationType);
      mixer.addEventListener('loop', onLoopCallback);
    }

    return (() => {
      if (onLoopCallback) {
        mixer.removeEventListener('loop', onLoopCallback);
      }
    });
  }, [animationType]);
  
  useEffect(() => {
    if (action) {
      action.reset().play();
      //action.reset().fadeIn(0.1).play();
      return (() => {
        action && action.fadeOut(0.1);
      });
    }
  }, [action, animationType, firstLoad]);

  const syncDrone = () => {
    if (droneRef.current && droneRef.current.parent && clockRef.current) {
      const time = clockRef.current.getElapsedTime();
      droneRef.current.parent.position.y = Math.cos( time ) * 0.1 + DRONE_BASE_Y;
      droneRef.current.parent.position.x = Math.sin( time ) * 0.05 + DRONE_BASE_X;
      droneRef.current.parent.position.z = Math.sin( time ) * 0.1;
      droneRef.current.parent.rotation.z = Math.sin( time ) * 0.1;
    }
  }

  const repaint = () => {
    requestAnimationFrame(repaint);
    randomBlink();
    speechToBlendshapes();
    if (managerVrm.current) {
      managerVrm.current.expressionManager.update();
    }
    syncDrone();
  }

  const randomBlink = () => {
    const vrm = managerVrm.current;
    if (!vrm) return;

    if (Date.now() - lastBlink.current > timeBeweetBlinks) {
      vrm.expressionManager.setValue('blinkLeft', 1);
      vrm.expressionManager.setValue('blinkRight', 1);
      lastBlink.current = Date.now();
    }

    if (Date.now() - lastBlink.current > 100) {
      vrm.expressionManager.setValue('blinkLeft', 0);
      vrm.expressionManager.setValue('blinkRight', 0);
    }
  }

  const speechToBlendshapes = () => {
    const vrm = managerVrm.current;
    if (!vrm) return;
    if (!lipsync.current) return;

    const blendshapes = lipsync.current.update();
    vrm.expressionManager.setValue("ih", blendshapes[0] || 0)
    vrm.expressionManager.setValue("ih", blendshapes[1] || 0)
    vrm.expressionManager.setValue("ou", blendshapes[2] || 0)
    vrm.expressionManager.setValue("oh", blendshapes[2] || 0)
  }

  if (controls) {
    controls.speak = (url) => {
      const vrm = managerVrm.current;
      if (!vrm) return;
      if (!lipsync.current) return;
      if (lipsync.current.working) {
        vrm.expressionManager.setValue("ih", 0)
        vrm.expressionManager.setValue("ih", 0)
        vrm.expressionManager.setValue("ou", 0)
        vrm.expressionManager.setValue("oh", 0)
        lipsync.current.stop();
      }
      lipsync.current.startSample(url);
    }

    controls.stopTalking = () => {
      const vrm = managerVrm.current;
      if (!vrm) return;
      if (!lipsync.current) return;
      if (lipsync.current.working) {
        vrm.expressionManager.setValue("ih", 0)
        vrm.expressionManager.setValue("ih", 0)
        vrm.expressionManager.setValue("ou", 0)
        vrm.expressionManager.setValue("oh", 0)
        lipsync.current.stop();
      }
    }
  }

  const findSkinnedMesh = (root, meshes) => {
    let childrenMeshes = root.children.filter(c => c.isMesh);
    meshes.push(...childrenMeshes);
    root.children.forEach(c => findSkinnedMesh(c, meshes));
  }
  const [outlined, setOutlined] = useState([]);
  useEffect(() => {
    lipsync.current = new Lipsync((durationMs) => onPlaybackStart(durationMs), () => onPlaybackStop());
    onLipsyncCreated(lipsync.current);
    managerVrm.current = userData.vrm;
    const allMeshes = [];
    findSkinnedMesh(scene, allMeshes);
    setOutlined(allMeshes.filter(m => !m.material.transparent));
    repaint();
  }, [userData.vrm, glbPath, weapon, secondaryWeapon, auxWeapon, secondaryLightWeapon, auxLightWeapon, object, armor, companion, drone]);

  return (
    <group {...props} ref={ref}>
      {scene && <primitive object={scene} position={[0,0,0]}/>}
      <EffectComposer multisampling={8} autoClear={false} depthBuffer={true}>
        <Outline selection={outlined} blendFunction={BlendFunction.ALPHA} selectionLayer={10} hiddenEdgeColor="black" visibleEdgeColor="black" edgeStrength={100} />
      </EffectComposer>
    </group>
  );
});

export default BaseFigure;
